<template>
  <div class="job-management-container">
    <!-- 搜索和添加区域 -->
    <div class="search-bar">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="任务名称">
          <el-input v-model="searchForm.jobName" placeholder="请输入任务名称" clearable />
        </el-form-item>
        <el-form-item label="任务分组">
          <el-select v-model="searchForm.jobGroup" placeholder="请选择分组" clearable>
            <el-option label="业务组" value="BUSINESS" />
            <el-option label="系统组" value="SYSTEM" />
            <el-option label="其他组" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="正常" value="0" />
            <el-option label="暂停" value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadJobList">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="success" @click="handleAdd">新增任务</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 任务列表 -->
    <el-table
        :data="jobList"
        v-loading="jobLoading"
        border
        style="width: 100%; margin-bottom: 20px"
    >
      <el-table-column prop="jobName" label="任务名称" width="120px" />
      <el-table-column prop="jobGroup" label="任务组" width="120">
        <template #default="scope">
          <el-tag size="small">{{ scope.row.jobGroup }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="invokeTarget" label="调用目标" width="180" show-overflow-tooltip />
      <el-table-column prop="cronExpression" label="Cron表达式" width="150" />
      <el-table-column prop="misfirePolicy" label="错失策略" width="100">
        <template #default="scope">
          <el-tag :type="getMisfireTagType(scope.row.misfirePolicy)" size="small">
            {{ getMisfireText(scope.row.misfirePolicy) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80">
        <template #default="scope">
          <el-tag :type="scope.row.status === '0' ? 'success' : 'warning'" size="small">
            {{ scope.row.status === '0' ? '正常' : '暂停' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="nextFireTime" label="下次执行" width="180" />
      <el-table-column prop="remark" label="备注" width="180" />
      <el-table-column label="操作" width="450" fixed="right">
        <template #default="scope">
          <el-button size="small" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="small" :type="scope.row.status === '0' ? 'warning' : 'success'" @click="toggleJobStatus(scope.row)">
            {{ scope.row.status === '0' ? '暂停' : '恢复' }}
          </el-button>
          <el-button size="small" type="info" @click="handleRun(scope.row)">执行一次</el-button>
          <el-button size="small" type="success" @click="handleManualTrigger(scope.row)">手动执行</el-button>
          <el-button size="small" type="info" @click="showJobLogs(scope.row)">日志</el-button>
          <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="action-bar">
      <el-button type="info" @click="showAllLogs">查看全部日志</el-button>
      <el-button type="warning" @click="showLogCleanDialog">清理日志</el-button>
    </div>

    <!-- 分页 -->
    <el-pagination
        v-model:current-page="jobPage.current"
        v-model:page-size="jobPage.size"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="jobPage.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
    />

    <!-- 新增/编辑对话框 -->
    <el-dialog
        v-model="dialogVisible"
        :title="dialogType === 'add' ? '新增任务' : '编辑任务'"
        width="600px"
        @closed="resetForm"
    >
      <el-form ref="jobFormRef" :model="jobForm" :rules="formRules" label-width="100px">
        <el-form-item label="任务名称" prop="jobName">
          <el-input v-model="jobForm.jobName" placeholder="请输入任务名称" />
        </el-form-item>

        <el-form-item label="任务分组" prop="jobGroup">
          <el-select v-model="jobForm.jobGroup" placeholder="请选择分组">
            <el-option label="业务组" value="BUSINESS" />
            <el-option label="系统组" value="SYSTEM" />
            <el-option label="其他组" value="OTHER" />
          </el-select>
        </el-form-item>

        <el-form-item label="调用目标" prop="invokeTarget">
          <el-input
              v-model="jobForm.invokeTarget"
              placeholder="请输入调用目标，如：com.phoneCall.phoneCall.job.task.ZyptJob"
              @focus="showInvokeTargetTips"
          >
            <template #append>
              <el-button @click="copyInvokeTarget">复制示例</el-button>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item label="Cron表达式" prop="cronExpression">
          <el-input v-model="jobForm.cronExpression" placeholder="如：0 0 10 * * ?">
            <template #append>
              <el-button @click="showCronDialog">生成Cron</el-button>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item label="错失策略" prop="misfirePolicy">
          <el-select v-model="jobForm.misfirePolicy" placeholder="请选择错失策略">
            <el-option label="立即执行" value="1" />
            <el-option label="执行一次" value="2" />
            <el-option label="放弃" value="3" />
          </el-select>
        </el-form-item>

        <el-form-item label="是否并发" prop="concurrent">
          <el-radio-group v-model="jobForm.concurrent">
            <el-radio label="0">禁止并发</el-radio>
            <el-radio label="1">允许并发</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="任务描述" prop="description">
          <el-input v-model="jobForm.description" type="textarea" :rows="3" placeholder="请输入任务描述" />
        </el-form-item>

        <el-form-item label="备注" prop="remark">
          <el-input v-model="jobForm.remark" type="textarea" :rows="2" placeholder="请输入备注" />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 调用目标提示对话框 -->
    <el-dialog v-model="invokeTargetTipsVisible" title="调用目标格式说明" width="600px">
      <div class="invoke-tips">
        <h4>调用目标格式说明：</h4>
        <p>调用目标必须是实现了 <code>org.quartz.Job</code> 接口的Spring Bean。</p>

        <h4>支持以下格式：</h4>
        <ul>
          <li><strong>Bean名称格式</strong>: <code>beanName.methodName</code></li>
          <li><strong>类全路径格式</strong>: <code>com.xxx.xxx.JobClass.methodName</code></li>
        </ul>

        <h4>当前系统可用任务：</h4>
        <div class="job-examples">
          <div class="example-item">
            <p><strong>Zypt任务</strong></p>
            <code>com.phoneCall.phoneCall.job.task.ZyptJob</code>
            <el-button size="small" type="text" @click="copyToForm('com.phoneCall.phoneCall.job.task.ZyptJob')">使用此</el-button>
          </div>
          <div class="example-item">
            <p><strong>Bean名称格式</strong></p>
            <code>zyptJob.execute</code>
            <el-button size="small" type="text" @click="copyToForm('zyptJob.execute')">使用此</el-button>
          </div>
        </div>

        <h4>注意事项：</h4>
        <ul>
          <li>确保任务类在Spring容器中注册为Bean</li>
          <li>任务类必须实现 <code>org.quartz.Job</code> 接口或继承 <code>BaseJob</code></li>
          <li>方法必须是公开的，且没有参数或只有一个 <code>JobExecutionContext</code> 参数</li>
        </ul>
      </div>
      <template #footer>
        <el-button @click="invokeTargetTipsVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- Cron表达式生成器对话框 -->
    <el-dialog v-model="cronDialogVisible" title="Cron表达式生成" width="800px">
      <div style="text-align: center; padding: 50px 0;">
        <p>建议使用在线Cron表达式生成器：</p>
        <p><a href="https://cron.qqe2.com/" target="_blank">https://cron.qqe2.com/</a></p>
        <p><a href="https://www.bejson.com/othertools/cron/" target="_blank">https://www.bejson.com/othertools/cron/</a></p>
        <p>生成后复制到上方输入框</p>
      </div>
      <template #footer>
        <el-button @click="cronDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 日志对话框 -->
    <el-dialog v-model="logDialogVisible" :title="logDialogTitle" width="90%" fullscreen @closed="closeLogDialog">
      <div class="log-filter">
        <el-form :inline="true" :model="logFilter">
          <el-form-item label="任务名称">
            <el-input v-model="logFilter.jobName" placeholder="输入任务名称筛选" clearable style="width: 200px" @clear="refreshJobLogList" />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="logFilter.status" placeholder="请选择状态" clearable>
              <el-option label="成功" value="0" />
              <el-option label="失败" value="1" />
            </el-select>
          </el-form-item>
          <el-form-item label="时间范围">
            <el-date-picker v-model="logFilter.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="refreshJobLogList">搜索</el-button>
            <el-button @click="resetLogFilter">重置</el-button>
            <el-button type="danger" @click="batchDeleteLogs" :disabled="selectedLogs.length === 0">批量删除</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table :data="jobLogList" v-loading="logLoading" border style="width: 100%; margin-top: 15px" @selection-change="handleLogSelectionChange">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="jobLogId" label="日志ID" width="100" />
        <el-table-column prop="jobName" label="任务名称" width="150" />
        <el-table-column prop="jobGroup" label="任务组" width="120">
          <template #default="scope">
            <el-tag size="small">{{ scope.row.jobGroup }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="invokeTarget" label="调用目标" width="200" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.status === '0' ? 'success' : 'danger'" size="small">
              {{ scope.row.status === '0' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="jobMessage" label="执行信息" min-width="200" show-overflow-tooltip />
<!--        <el-table-column label="执行参数" width="200">-->
<!--          <template #default="scope">-->
<!--            <el-tooltip-->
<!--                v-if="scope.row.jobParams"-->
<!--                :content="formatJobParams(scope.row.jobParams)"-->
<!--                placement="top"-->
<!--            >-->
<!--              <el-button type="text" size="small">查看参数</el-button>-->
<!--            </el-tooltip>-->
<!--            <span v-else>-</span>-->
<!--          </template>-->
<!--        </el-table-column>-->
        <el-table-column prop="exceptionInfo" label="异常信息" min-width="250" show-overflow-tooltip />
        <el-table-column prop="createTime" label="执行时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="scope">
            <el-button size="small" type="text" @click="viewLogDetail(scope.row)" v-if="scope.row.exceptionInfo">查看详情</el-button>
            <el-button size="small" type="text" style="color: #f56c6c" @click="deleteSingleLog(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
          v-model:current-page="logPage.current"
          v-model:page-size="logPage.size"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="logPage.total"
          @size-change="handleLogSizeChange"
          @current-change="handleLogCurrentChange"
          style="margin-top: 15px"
      />
    </el-dialog>

    <!-- 日志详情对话框 -->
    <el-dialog v-model="logDetailVisible" title="异常详情" width="70%">
      <div class="exception-detail">
        <h4>任务信息</h4>
        <p><strong>任务名称:</strong> {{ currentLog.jobName }}</p>
        <p><strong>执行时间:</strong> {{ formatDateTime(currentLog.createTime) }}</p>
        <p><strong>执行状态:</strong>
          <el-tag :type="currentLog.status === '0' ? 'success' : 'danger'" size="small">
            {{ currentLog.status === '0' ? '成功' : '失败' }}
          </el-tag>
        </p>

        <h4 style="margin-top: 20px">异常堆栈</h4>
        <pre class="exception-stack">{{ currentLog.exceptionInfo }}</pre>
      </div>
      <template #footer>
        <el-button @click="logDetailVisible = false">关闭</el-button>
        <el-button type="primary" @click="copyException">复制</el-button>
      </template>
    </el-dialog>

    <!-- 清理日志对话框 -->
    <el-dialog v-model="cleanLogDialogVisible" title="清理任务日志" width="500px">
      <el-form :model="cleanLogForm" label-width="100px">
        <el-form-item label="清理范围">
          <el-radio-group v-model="cleanLogForm.scope">
            <el-radio label="all">全部日志</el-radio>
            <el-radio label="job">指定任务</el-radio>
            <el-radio label="date">按时间清理</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="任务名称" v-if="cleanLogForm.scope === 'job'">
          <el-select v-model="cleanLogForm.jobName" placeholder="请选择任务" filterable style="width: 100%">
            <el-option v-for="job in jobList" :key="job.jobId" :label="job.jobName" :value="job.jobName" />
          </el-select>
        </el-form-item>

        <el-form-item label="保留天数" v-if="cleanLogForm.scope === 'date'">
          <el-input-number v-model="cleanLogForm.days" :min="1" :max="365" placeholder="请输入天数" />
          <span style="margin-left: 10px">天前的日志将被清理</span>
        </el-form-item>

        <el-form-item label="确认操作">
          <el-alert type="warning" :closable="false">此操作将永久删除日志，无法恢复！</el-alert>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="cleanLogDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmCleanLog" :loading="cleanLogLoading">确认清理</el-button>
      </template>
    </el-dialog>

    <!-- 手动执行对话框 -->
    <el-dialog v-model="triggerDialogVisible" :title="`手动执行任务 - ${currentJob.jobName}`" width="600px" @closed="resetTriggerForm">
      <el-form ref="triggerFormRef" :model="triggerForm" label-width="120px">
        <el-alert type="info" :closable="false" style="margin-bottom: 20px">
          手动执行会立即触发一次任务执行，可以传递自定义参数
        </el-alert>

        <el-form-item label="开始时间">
          <el-date-picker v-model="triggerForm.startTime" type="datetime" placeholder="选择开始时间" format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <el-form-item label="结束时间">
          <el-date-picker v-model="triggerForm.endTime" type="datetime" placeholder="选择结束时间" format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <el-form-item label="自定义参数">
          <div class="custom-params">
            <div v-for="(param, index) in triggerForm.customParams" :key="index" class="param-item">
              <el-input v-model="param.key" placeholder="参数名" style="width: 180px; margin-right: 10px" />
              <el-input v-model="param.value" placeholder="参数值" style="width: 180px" />
              <el-button type="danger" icon="Delete" circle size="small" @click="removeParam(index)" style="margin-left: 10px" />
            </div>
            <el-button type="primary" @click="addParam" size="small" icon="Plus">添加参数</el-button>
          </div>
        </el-form-item>

        <el-form-item label="参数预览">
          <el-card shadow="never" style="background: #f8f9fa;">
            <pre style="margin: 0; font-family: monospace;">{{ JSON.stringify(triggerParamsPreview, null, 2) }}</pre>
          </el-card>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="triggerDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmManualTrigger" :loading="triggerLoading">立即执行</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import {
  getJobList,
  getJobDetail,
  addJob,
  updateJob,
  deleteJob,
  pauseJob,
  resumeJob,
  runJob,
  getJobLogList,
  deleteJobLog,
  clearJobLog,
  triggerJob
} from '../../api/job/job.js'
import { ElMessage, ElMessageBox } from 'element-plus'

// 搜索表单
const searchForm = reactive({
  jobName: '',
  jobGroup: '',
  status: ''
})

// 任务列表相关
const jobList = ref([])
const jobLoading = ref(false)
const jobPage = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 对话框相关
const dialogVisible = ref(false)
const cronDialogVisible = ref(false)
const dialogType = ref('add')
const formLoading = ref(false)
const jobFormRef = ref(null)
const invokeTargetTipsVisible = ref(false)

// 任务表单
const jobForm = reactive({
  jobId: null,
  jobName: '',
  jobGroup: 'BUSINESS',
  invokeTarget: 'zyptJob.execute',
  cronExpression: '',
  misfirePolicy: '1',
  concurrent: '0',
  status: '0',
  description: '',
  remark: ''
})

// 手动执行相关变量
const triggerDialogVisible = ref(false)
const triggerLoading = ref(false)
const triggerFormRef = ref(null)
const currentJob = ref({})
const triggerForm = reactive({
  startTime: '',
  endTime: '',
  customParams: []
})

// 计算属性：参数预览
const triggerParamsPreview = computed(() => {
  const params = {}
  if (triggerForm.startTime) {
    params.startTime = new Date(triggerForm.startTime).getTime()
  }
  if (triggerForm.endTime) {
    params.endTime = new Date(triggerForm.endTime).getTime()
  }
  triggerForm.customParams.forEach(param => {
    if (param.key && param.value) {
      if (!isNaN(param.value) && param.value.trim() !== '') {
        params[param.key] = Number(param.value)
      } else {
        params[param.key] = param.value
      }
    }
  })
  return params
})

// 日志相关变量
const logDialogVisible = ref(false)
const logDialogTitle = ref('任务执行日志')
const logLoading = ref(false)
const logDetailVisible = ref(false)
const cleanLogDialogVisible = ref(false)
const cleanLogLoading = ref(false)
const jobLogList = ref([])
const selectedLogs = ref([])
const currentLog = ref({})
const logFilter = reactive({
  jobName: '',
  status: '',
  dateRange: []
})
const cleanLogForm = reactive({
  scope: 'all',
  jobName: '',
  days: 30
})
const logPage = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 表单验证规则
const formRules = {
  jobName: [
    { required: true, message: '请输入任务名称', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符', trigger: 'blur' }
  ],
  jobGroup: [
    { required: true, message: '请选择任务分组', trigger: 'change' }
  ],
  invokeTarget: [
    { required: true, message: '请输入调用目标', trigger: 'blur' },
    { validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error('调用目标不能为空'))
        } else if (value.trim().length < 5) {
          callback(new Error('调用目标格式不正确'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  cronExpression: [
    { required: true, message: '请输入Cron表达式', trigger: 'blur' }
  ]
}

// 初始化加载
onMounted(() => {
  loadJobList()
})

// 加载任务列表
const loadJobList = async () => {
  try {
    jobLoading.value = true
    const params = {
      current: jobPage.current,
      size: jobPage.size,
      ...searchForm
    }
    const response = await getJobList(params)
    if (response.code === 200) {
      jobList.value = response.data.records || []
      jobPage.total = response.data.total || 0
    } else {
      ElMessage.error(response.message || '获取任务列表失败')
    }
  } catch (error) {
    console.error('加载任务列表失败:', error)
    ElMessage.error('加载失败，请检查网络连接')
  } finally {
    jobLoading.value = false
  }
}

// 重置搜索
const resetSearch = () => {
  searchForm.jobName = ''
  searchForm.jobGroup = ''
  searchForm.status = ''
  loadJobList()
}

// 新增任务
const handleAdd = () => {
  dialogType.value = 'add'
  resetForm()
  dialogVisible.value = true
}

// 编辑任务
const handleEdit = async (row) => {
  try {
    const response = await getJobDetail(row.jobId)
    if (response.code === 200) {
      Object.assign(jobForm, response.data)
      dialogType.value = 'edit'
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取任务详情失败:', error)
    ElMessage.error('获取任务详情失败')
  }
}

const formatJobParams = (params) => {
  if (!params) return '无参数';
  try {
    if (typeof params === 'string') {
      if (params.startsWith('{')) {
        const parsed = JSON.parse(params);
        return JSON.stringify(parsed, null, 2);
      }
    }
    return params;
  } catch (e) {
    return params;
  }
};
// 切换任务状态
const toggleJobStatus = async (row) => {
  try {
    if (row.status === '0') {
      await pauseJob(row.jobId)
      ElMessage.success('任务已暂停')
    } else {
      await resumeJob(row.jobId)
      ElMessage.success('任务已恢复')
    }
    loadJobList()
  } catch (error) {
    console.error('切换任务状态失败:', error)
    ElMessage.error(error.message || '操作失败')
  }
}

// 立即执行一次
const handleRun = async (row) => {
  try {
    await ElMessageBox.confirm('确定要立即执行一次该任务吗？', '提示', {
      type: 'warning'
    })
    await runJob(row.jobId)
    ElMessage.success('任务已开始执行，请查看日志')
    setTimeout(() => {
      showJobLogs(row)
    }, 1000)
  } catch (error) {
    if (error !== 'cancel') {
      console.error('执行任务失败:', error)
      ElMessage.error(error.message || '执行失败')
    }
  }
}

// 删除任务
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该任务吗？', '提示', {
      type: 'warning'
    })
    await deleteJob(row.jobId)
    ElMessage.success('删除成功')
    loadJobList()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除任务失败:', error)
      ElMessage.error(error.message || '删除失败')
    }
  }
}

// 提交表单
const submitForm = async () => {
  if (!jobFormRef.value) return
  try {
    await jobFormRef.value.validate()
    formLoading.value = true
    if (dialogType.value === 'add') {
      await addJob(jobForm)
      ElMessage.success('添加成功')
    } else {
      await updateJob(jobForm)
      ElMessage.success('修改成功')
    }
    dialogVisible.value = false
    loadJobList()
  } catch (error) {
    console.error('保存任务失败:', error)
    ElMessage.error(error.message || '保存失败')
  } finally {
    formLoading.value = false
  }
}

// 重置表单
const resetForm = () => {
  if (jobFormRef.value) {
    jobFormRef.value.resetFields()
  }
  Object.assign(jobForm, {
    jobId: null,
    jobName: '',
    jobGroup: 'BUSINESS',
    invokeTarget: 'zyptJob.execute',
    cronExpression: '',
    misfirePolicy: '1',
    concurrent: '0',
    status: '0',
    description: '',
    remark: ''
  })
}

// 显示Cron对话框
const showCronDialog = () => {
  cronDialogVisible.value = true
}

// 分页事件
const handleSizeChange = (size) => {
  jobPage.size = size
  loadJobList()
}

const handleCurrentChange = (current) => {
  jobPage.current = current
  loadJobList()
}

// 调用目标相关
const showInvokeTargetTips = () => {
  invokeTargetTipsVisible.value = true
}

const copyToForm = (value) => {
  jobForm.invokeTarget = value
  invokeTargetTipsVisible.value = false
  ElMessage.success('已复制到输入框')
}

const copyInvokeTarget = async () => {
  try {
    const example = 'zyptJob.execute'
    await navigator.clipboard.writeText(example)
    ElMessage.success(`已复制示例: ${example}`)
    jobForm.invokeTarget = example
  } catch (error) {
    console.error('复制失败:', error)
    const textArea = document.createElement('textarea')
    textArea.value = 'zyptJob.execute'
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    ElMessage.success('已复制示例: zyptJob.execute')
    jobForm.invokeTarget = 'zyptJob.execute'
  }
}

// 手动执行相关
const handleManualTrigger = (job) => {
  currentJob.value = job
  resetTriggerForm()
  triggerDialogVisible.value = true
}

const addParam = () => {
  triggerForm.customParams.push({ key: '', value: '' })
}

const removeParam = (index) => {
  triggerForm.customParams.splice(index, 1)
}

const resetTriggerForm = () => {
  triggerForm.startTime = ''
  triggerForm.endTime = ''
  triggerForm.customParams = []
  addParam()
}

const confirmManualTrigger = async () => {
  try {
    triggerLoading.value = true
    const params = {}
    if (triggerForm.startTime) {
      params.startTime = new Date(triggerForm.startTime).getTime()
    }
    if (triggerForm.endTime) {
      params.endTime = new Date(triggerForm.endTime).getTime()
    }
    triggerForm.customParams.forEach(param => {
      if (param.key && param.value) {
        if (!isNaN(param.value) && param.value.trim() !== '') {
          params[param.key] = Number(param.value)
        } else {
          params[param.key] = param.value
        }
      }
    })
    const response = await triggerJob({
      jobId: currentJob.value.jobId,
      ...params
    })
    ElMessage.success('任务已触发，开始执行')
    triggerDialogVisible.value = false
    ElMessage({
      type: 'success',
      message: response || '任务触发成功',
      duration: 5000
    })
    setTimeout(() => {
      showJobLogs(currentJob.value)
    }, 1000)
  } catch (error) {
    console.error('手动执行任务失败:', error)
    ElMessage.error(error.message || '任务执行失败')
  } finally {
    triggerLoading.value = false
  }
}

// 日志相关
const showJobLogs = (job) => {
  logDialogTitle.value = `任务日志 - ${job.jobName}`
  logFilter.jobName = job.jobName
  logPage.current = 1
  logDialogVisible.value = true
  loadJobLogList()
}

const showAllLogs = () => {
  logDialogTitle.value = '全部任务日志'
  logFilter.jobName = ''
  logPage.current = 1
  logDialogVisible.value = true
  loadJobLogList()
}

const loadJobLogList = async () => {
  try {
    logLoading.value = true
    const params = {
      current: logPage.current,
      size: logPage.size,
      jobName: logFilter.jobName || undefined,
      status: logFilter.status || undefined
    }
    if (logFilter.dateRange && logFilter.dateRange.length === 2) {
      params.startTime = logFilter.dateRange[0] + ' 00:00:00'
      params.endTime = logFilter.dateRange[1] + ' 23:59:59'
    }
    const response = await getJobLogList(params)
    if (response.code === 200) {
      jobLogList.value = response.data.records || []
      logPage.total = response.data.total || 0
    } else {
      ElMessage.error(response.message || '获取任务日志失败')
    }
  } catch (error) {
    console.error('加载任务日志失败:', error)
    ElMessage.error('加载日志失败，请检查网络连接')
  } finally {
    logLoading.value = false
  }
}

const refreshJobLogList = () => {
  logPage.current = 1
  loadJobLogList()
}

const resetLogFilter = () => {
  logFilter.jobName = ''
  logFilter.status = ''
  logFilter.dateRange = []
  refreshJobLogList()
}

const handleLogSelectionChange = (selection) => {
  selectedLogs.value = selection
}

const viewLogDetail = (log) => {
  currentLog.value = log
  logDetailVisible.value = true
}

const deleteSingleLog = async (log) => {
  try {
    await ElMessageBox.confirm('确定要删除这条日志吗？', '提示', {
      type: 'warning'
    })
    const response = await deleteJobLog(log.jobLogId)
    if (response.code === 200) {
      ElMessage.success('删除成功')
      loadJobLogList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除日志失败:', error)
      ElMessage.error(error.message || '删除失败')
    }
  }
}

const batchDeleteLogs = async () => {
  try {
    await ElMessageBox.confirm(`确定要删除选中的 ${selectedLogs.value.length} 条日志吗？`, '提示', {
      type: 'warning'
    })
    const promises = selectedLogs.value.map(log => deleteJobLog(log.jobLogId))
    await Promise.all(promises)
    ElMessage.success('批量删除成功')
    selectedLogs.value = []
    loadJobLogList()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('批量删除日志失败:', error)
      ElMessage.error(error.message || '批量删除失败')
    }
  }
}

const showLogCleanDialog = () => {
  cleanLogForm.scope = 'all'
  cleanLogForm.jobName = ''
  cleanLogForm.days = 30
  cleanLogDialogVisible.value = true
}

const confirmCleanLog = async () => {
  try {
    await ElMessageBox.confirm('确定要清理日志吗？此操作不可恢复！', '警告', {
      type: 'warning',
      confirmButtonText: '确定清理',
      cancelButtonText: '取消'
    })
    cleanLogLoading.value = true
    let jobName = null
    if (cleanLogForm.scope === 'job' && cleanLogForm.jobName) {
      jobName = cleanLogForm.jobName
    }
    const response = await clearJobLog(jobName)
    if (response.code === 200) {
      ElMessage.success('日志清理成功')
      cleanLogDialogVisible.value = false
      if (logDialogVisible.value) {
        refreshJobLogList()
      }
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('清理日志失败:', error)
      ElMessage.error(error.message || '清理失败')
    }
  } finally {
    cleanLogLoading.value = false
  }
}

const copyException = async () => {
  try {
    await navigator.clipboard.writeText(currentLog.value.exceptionInfo)
    ElMessage.success('异常信息已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    const textArea = document.createElement('textarea')
    textArea.value = currentLog.value.exceptionInfo
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    ElMessage.success('异常信息已复制')
  }
}

const closeLogDialog = () => {
  logDialogVisible.value = false
  jobLogList.value = []
  selectedLogs.value = []
  logFilter.jobName = ''
  logFilter.status = ''
  logFilter.dateRange = []
  logPage.current = 1
  logPage.total = 0
}

const handleLogSizeChange = (size) => {
  logPage.size = size
  loadJobLogList()
}

const handleLogCurrentChange = (current) => {
  logPage.current = current
  loadJobLogList()
}

// 工具方法
const getMisfireText = (policy) => {
  const map = { '1': '立即执行', '2': '执行一次', '3': '放弃' }
  return map[policy] || '未知'
}

const getMisfireTagType = (policy) => {
  const map = { '1': 'success', '2': 'info', '3': 'warning' }
  return map[policy] || 'info'
}

const formatDateTime = (datetime) => {
  if (!datetime) return ''
  try {
    const date = new Date(datetime)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-')
  } catch (e) {
    return datetime
  }
}
</script>

<style scoped>
.job-management-container {
  padding: 20px;
  background-color: #f5f7fa;
}

.search-bar {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.action-bar {
  margin: 15px 0;
  display: flex;
  gap: 10px;
}

.el-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.custom-params {
  width: 100%;
}

.param-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.log-filter {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.exception-detail {
  line-height: 1.6;
}

.exception-detail h4 {
  margin: 15px 0 10px 0;
  color: #409eff;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
}

.exception-stack {
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  max-height: 400px;
  overflow: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

.invoke-tips {
  line-height: 1.6;
}

.invoke-tips h4 {
  margin: 15px 0 10px 0;
  color: #409eff;
}

.invoke-tips ul {
  padding-left: 20px;
  margin: 10px 0;
}

.invoke-tips code {
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  color: #e96900;
}

.job-examples {
  margin: 15px 0;
}

.example-item {
  background: #f8f9fa;
  border-left: 4px solid #409eff;
  padding: 10px 15px;
  margin: 10px 0;
  border-radius: 4px;
}

.example-item p {
  margin: 0 0 5px 0;
  font-weight: bold;
}

.example-item code {
  display: block;
  margin: 5px 0;
  padding: 8px;
  background: #e8f4ff;
  border-radius: 4px;
  font-size: 14px;
}
</style>